
import React, { useState, useEffect } from 'react';
import imageCompression from 'browser-image-compression';
import { toFarsiNumber, formatFarsiPrice } from '../farsiNumber';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import Stack from '@mui/material/Stack';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import Typography from '@mui/material/Typography';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import axios from '../api';
import { CUSTOM_AD_PROPERTIES } from '../custom_ad_properties';
import { CATEGORY_CONDITION_LEAF_IDS } from '../categoryConditionLeafIds';

// Helper to convert Farsi digits and comma to English, then remove all non-digit chars
function faToEn(s) {
  if (!s) return '';
  return String(s)
    .replace(/[۰-۹]/g, d => String.fromCharCode(d.charCodeAt(0) - 1728))
    .replace(/[،,]/g, ',')
    .replace(/[^0-9]/g, '');
}

const getCategoryName = (categories, id) => {
  for (const cat of categories) {
    if (cat.id === id) return cat.name;
    if (cat.children) {
      for (const sub of cat.children) {
        if (sub.id === id) return sub.name;
        if (sub.children) {
          for (const subsub of sub.children) {
            if (subsub.id === id) return subsub.name;
          }
        }
      }
    }
  }
  return '';
};

const AdForm = ({ adId, initialData, selectedCity }) => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    title: initialData?.title || '',
    description: initialData?.description || '',
    price: initialData?.price || '',
    priceDisplay: initialData?.price ? formatFarsiPrice(initialData.price) : '',
    deposit: initialData?.deposit || '',
    depositDisplay: initialData?.deposit ? formatFarsiPrice(initialData.deposit) : '',
    monthly_rent: initialData?.monthly_rent || '',
    monthly_rentDisplay: initialData?.monthly_rent ? formatFarsiPrice(initialData.monthly_rent) : '',
    city: initialData?.city || '',
    category: initialData?.category || '',
    condition: initialData?.condition || '',
    images: [], // new images (File objects)
  });

  // Set default city from selectedCity if not editing and city is not set
  useEffect(() => {
    if (!adId && !form.city && selectedCity) {
      setForm(prev => ({ ...prev, city: selectedCity }));
    }
    // eslint-disable-next-line
  }, [adId, form.city, selectedCity]);
  // State for custom fields
  const [customFields, setCustomFields] = useState(initialData?.custom_fields || {});
  // When editing, update customFields if initialData.custom_fields changes (e.g. after async fetch)
  useEffect(() => {
    if (initialData?.custom_fields) {
      setCustomFields(initialData.custom_fields);
    }
  }, [initialData?.custom_fields]);
  // Existing images from backend
  const [existingImages, setExistingImages] = useState(initialData?.images || []);
  const [removedImageIds, setRemovedImageIds] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]); // local preview URLs for new images

  const [categories, setCategories] = useState([]);
  const [cities, setCities] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [subAnchorEl, setSubAnchorEl] = useState(null);
  const [subSubAnchorEl, setSubSubAnchorEl] = useState(null);
  const [selectedCat, setSelectedCat] = useState({ main: null, sub: null, subsub: null });
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get('/categories/').then(res => setCategories(res.data));
    axios.get('/cities/').then(res => setCities(res.data));
  }, []);

  // Handles all fields except price/deposit/monthly_rent
  const handleChange = async (e) => {
    const { name, value, files } = e.target;
    if (name === 'images' && files) {
      const fileArr = Array.from(files);
      const compressedFiles = await Promise.all(
        fileArr.map(async (file) => {
          try {
            console.log(`Original file: ${file.name}, size: ${file.size / 1024 / 1024} MB`);
            const options = { maxSizeMB: 1, useWebWorker: true };
            const compressed = await imageCompression(file, options);
            console.log(`Compressed file: ${compressed.name}, size: ${compressed.size / 1024 / 1024} MB`);
            if (compressed.size > 2 * 1024 * 1024) {
              alert('تصویر پس از فشرده‌سازی هنوز بزرگ‌تر از 2MB است و ممکن است آپلود نشود.');
            }
            return compressed;
          } catch (err) {
            console.warn('Image compression failed:', err);
            alert('فشرده‌سازی تصویر انجام نشد. تصویر اصلی ارسال می‌شود.');
            return file;
          }
        })
      );
      setForm(prev => ({ ...prev, images: [...prev.images, ...compressedFiles] }));
      setImagePreviews(prev => [
        ...prev,
        ...compressedFiles.map(file => URL.createObjectURL(file))
      ]);
    } else if (["price", "deposit", "monthly_rent"].includes(name)) {
      handlePriceFieldChange(name, value);
    } else {
      setForm(prev => ({ ...prev, [name]: value }));
    }
  };

  // Special handler for price/deposit/monthly_rent to allow live Farsi formatting
  const handlePriceFieldChange = (name, value) => {
    // Accept both Farsi and English digits, remove all non-digits
    const enValue = value.replace(/[۰-۹]/g, d => String.fromCharCode(d.charCodeAt(0) - 1728)).replace(/[^0-9]/g, '');
    // Format with English comma, then convert to Farsi digits and comma
    const withComma = enValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const enToFa = s => s.replace(/[0-9]/g, d => String.fromCharCode(d.charCodeAt(0) + 1728));
    const faValue = enToFa(withComma).replace(/,/g, '،');
    // Store both raw and display value
    setForm(prev => ({
      ...prev,
      [name]: enValue,
      [`${name}Display`]: faValue
    }));
  };

  // Remove existing image
  const handleRemoveExistingImage = (imgId) => {
    setRemovedImageIds(prev => [...prev, imgId]);
    setExistingImages(prev => prev.filter(img => img.id !== imgId));
  };

  const handleSelectChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  // Category menu handlers
  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => {
    setAnchorEl(null);
    setSubAnchorEl(null);
    setSubSubAnchorEl(null);
  };
  const handleMainCatHover = (event, cat) => {
    setSelectedCat({ main: cat, sub: null, subsub: null });
    setSubAnchorEl(event.currentTarget);
    setSubSubAnchorEl(null); // Close sub-sub menu if open

    // If another main category is hovered, close previous submenu
    if (selectedCat.main?.id !== cat.id) {
      setSubAnchorEl(event.currentTarget);
    }
  };
  const handleSubCatHover = (event, sub) => {
    setSelectedCat(prev => ({ ...prev, sub, subsub: null }));
    setSubSubAnchorEl(event.currentTarget);
  };
  const handleCatSelect = (catId) => {
    setForm(prev => ({ ...prev, category: catId }));
    // Reset custom fields when category changes
    setCustomFields({});
    handleMenuClose();
  };

  const [submitting, setSubmitting] = useState(false);
  const handleCustomFieldChange = (name, value) => {
    setCustomFields(prev => ({ ...prev, [name]: value }));
  };

  // Show and require condition only if category is in the leaf list
  const showCondition = CATEGORY_CONDITION_LEAF_IDS.includes(Number(form.category));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) {
      console.warn('AdForm: Prevented double submission');
      return;
    }
    setSubmitting(true);
    setError('');
    const token = localStorage.getItem('token');
    // Always send a default price (required by backend)
  let priceToSend = faToEn(form.price);
  let depositToSend = faToEn(form.deposit);
  let monthlyRentToSend = faToEn(form.monthly_rent);
    if (form.category == 3) {
      // For apartment rental, use deposit or monthly_rent or fallback to 0
      priceToSend = depositToSend || monthlyRentToSend || 0;
    }
    let conditionToSend = form.condition;
    if (showCondition) {
      if (!form.condition) {
        setError('لطفاً وضعیت کالا را انتخاب کنید.');
        setSubmitting(false);
        return;
      }
    } else {
      conditionToSend = 'نو';
    }
    // Validate required fields (skip condition check, since we handled it above)
    if (!form.title || !form.description || !form.category || !form.city) {
      setError('لطفاً همه فیلدهای ضروری را پر کنید.');
      setSubmitting(false);
      return;
    }
    if (priceToSend === '' || priceToSend === null || isNaN(Number(priceToSend))) {
      setError('لطفاً مبلغ معتبر وارد کنید.');
      setSubmitting(false);
      return;
    }
    // Always include deposit and monthly_rent for category 3 (اجاره)
    const adData = {
      ...form,
      category: Number(form.category),
      city: Number(form.city),
      condition: conditionToSend,
      removed_images: removedImageIds, // send removed image IDs to backend
      custom_fields: Object.keys(customFields).length > 0 ? customFields : undefined
    };
    // Only add price if non-empty and valid
    if (priceToSend && !isNaN(Number(priceToSend))) adData.price = Number(priceToSend);
    else delete adData.price;
    if (form.category == 3) {
      if (depositToSend && !isNaN(Number(depositToSend))) adData.deposit = Number(depositToSend);
      else delete adData.deposit;
      if (monthlyRentToSend && !isNaN(Number(monthlyRentToSend))) adData.monthly_rent = Number(monthlyRentToSend);
      else delete adData.monthly_rent;
    }
    delete adData.images;
    delete adData.priceDisplay;
    delete adData.depositDisplay;
    delete adData.monthly_rentDisplay;
    let adIdLocal = adId || null;
    try {
      if (adIdLocal) {
        // Edit mode: PATCH ad
        await axios.patch(`/ads/${adIdLocal}/`, adData, {
          headers: {
            ...(token && { Authorization: `Bearer ${token}` })
          }
        });
      } else {
        // Create mode: POST ad
        const adRes = await axios.post('/ads/', adData, {
          headers: {
            ...(token && { Authorization: `Bearer ${token}` })
          }
        });
        adIdLocal = adRes.data.id;
      }
    } catch (err) {
      setError('خطا در ثبت آگهی.');
      setSubmitting(false);
      return;
    }
    // Do not send separate DELETE requests for images; rely on PATCH with removed_images array
    // Upload new images
    for (const file of form.images) {
      const data = new FormData();
      data.append('image', file);
      data.append('ad_id', adIdLocal);
      try {
        await axios.post('/ads/upload-image/', data, {
          headers: {
            'Content-Type': 'multipart/form-data',
            ...(token && { Authorization: `Bearer ${token}` })
          }
        });
      } catch {}
    }
    setSubmitting(false);
    navigate('/');
  };

  return (
    <Box
      component="form"
      onSubmit={async (e) => {
        e.preventDefault();
        // Prevent update if no image present (edit mode only)
        if (adId && existingImages.length === 0 && form.images.length === 0) {
          setError('حداقل یک تصویر باید انتخاب شود.');
          return;
        }
        await handleSubmit(e);
      }}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        bgcolor: 'background.paper',
        borderRadius: 2,
        boxShadow: 3,
        p: 3,
        direction: 'rtl',
      }}
    >
      {error && (
        <Typography color="error" align="right" sx={{ mb: 1, fontWeight: 700 }}>
          {error}
        </Typography>
      )}

      <Typography variant="h6" fontWeight={700} color="primary" align="right" gutterBottom>
        اطلاعات آگهی
      </Typography>

      {/* Move city and category selectors to the top */}
      <FormControl fullWidth>
        <InputLabel id="city-label" sx={{ fontFamily: 'Vazirmatn', color: 'primary.main' }} shrink>
          شهر
        </InputLabel>
        <Select
          labelId="city-label"
          name="city"
          value={form.city}
          onChange={handleSelectChange}
          displayEmpty
          label="شهر"
          sx={{ fontFamily: 'Vazirmatn', color: 'primary.main' }}
        >
          <MenuItem value="" sx={{ fontFamily: 'Vazirmatn', color: 'primary.main' }}><em>انتخاب شهر</em></MenuItem>
          {cities.map(city => (
            <MenuItem key={city.id} value={city.id} sx={{ fontFamily: 'Vazirmatn', color: 'primary.main' }}>{city.name}</MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Category Selector */}
      <FormControl fullWidth>
        <Button variant="outlined" onClick={handleMenuOpen} sx={{ textAlign: 'right', fontFamily: 'Vazirmatn' }}>
          {form.category
            ? `دسته‌بندی انتخاب شده: ${getCategoryName(categories, form.category)}`
            : 'انتخاب دسته‌بندی'}
        </Button>

        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={{ style: { position: 'absolute', left: 0, top: 40, minWidth: 200, zIndex: 1300 } }}>
          {categories.map(cat => (
            <MenuItem
              key={cat.id}
              onClick={cat.children?.length ? (e) => handleMainCatHover(e, cat) : () => handleCatSelect(cat.id)}
              sx={{
                justifyContent: 'space-between',
                fontFamily: 'Vazirmatn',
                bgcolor: form.category == cat.id ? 'primary.light' : undefined,
                color: form.category == cat.id ? 'primary.contrastText' : undefined,
                fontWeight: form.category == cat.id ? 700 : undefined
              }}
            >
              {cat.children?.length ? <ArrowLeftIcon fontSize="small" style={{ marginLeft: 8 }} /> : null}
              {cat.name}
            </MenuItem>
          ))}
        </Menu>

        {selectedCat.main?.children?.length > 0 && (
          <Menu anchorEl={subAnchorEl} open={Boolean(subAnchorEl)} onClose={handleMenuClose}
            anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{ style: { position: 'absolute', left: 210, top: 40, minWidth: 200, zIndex: 1300 } }}>
            {selectedCat.main.children.map(sub => (
              <MenuItem
                key={sub.id}
                onClick={sub.children?.length ? (e) => handleSubCatHover(e, sub) : () => handleCatSelect(sub.id)}
                sx={{
                  justifyContent: 'space-between',
                  fontFamily: 'Vazirmatn',
                  bgcolor: form.category == sub.id ? 'primary.light' : undefined,
                  color: form.category == sub.id ? 'primary.contrastText' : undefined,
                  fontWeight: form.category == sub.id ? 700 : undefined
                }}
              >
                {sub.children?.length ? <ArrowLeftIcon fontSize="small" style={{ marginLeft: 8 }} /> : null}
                {sub.name}
              </MenuItem>
            ))}
          </Menu>
        )}

        {selectedCat.sub?.children?.length > 0 && (
          <Menu anchorEl={subSubAnchorEl} open={Boolean(subSubAnchorEl)} onClose={handleMenuClose}
            anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{ style: { position: 'absolute', left: 420, top: 40, minWidth: 200, zIndex: 1300 } }}>
            {selectedCat.sub.children.map(subsub => (
              <MenuItem
                key={subsub.id}
                onClick={() => handleCatSelect(subsub.id)}
                sx={{
                  fontFamily: 'Vazirmatn',
                  bgcolor: form.category == subsub.id ? 'primary.light' : undefined,
                  color: form.category == subsub.id ? 'primary.contrastText' : undefined,
                  fontWeight: form.category == subsub.id ? 700 : undefined
                }}
              >
                {subsub.name}
              </MenuItem>
            ))}
          </Menu>
        )}
      </FormControl>

      <TextField
        name="title"
        value={toFarsiNumber(form.title)}
        onChange={handleChange}
        label="عنوان"
        variant="outlined"
        fullWidth
        inputProps={{ style: { textAlign: 'right' } }}
      />
      <TextField
        name="description"
        value={toFarsiNumber(form.description)}
        onChange={handleChange}
        label="توضیحات"
        variant="outlined"
        multiline
        rows={3}
        fullWidth
        inputProps={{ style: { textAlign: 'right' } }}
      />
      {/* Apartment rental: show ودیعه and اجاره ی ماهیانه instead of price */}
      {form.category == 3 ? (
        <>
          <TextField
            name="deposit"
            value={form.depositDisplay}
            onChange={handleChange}
            label="ودیعه (تومان)"
            variant="outlined"
            fullWidth
            inputProps={{ style: { textAlign: 'right' }, inputMode: 'numeric' }}
            sx={{ mb: 2 }}
          />
          <TextField
            name="monthly_rent"
            value={form.monthly_rentDisplay}
            onChange={handleChange}
            label="اجاره ی ماهیانه (تومان)"
            variant="outlined"
            fullWidth
            inputProps={{ style: { textAlign: 'right' }, inputMode: 'numeric' }}
          />
        </>
      ) : (
        <TextField
          name="price"
          value={form.priceDisplay}
          onChange={handleChange}
          label="قیمت (تومان)"
          variant="outlined"
          fullWidth
          inputProps={{ style: { textAlign: 'right' }, inputMode: 'numeric' }}
        />
      )}

      {/* Condition dropdown if required for this category (single location, UI/logic preserved) */}
      {showCondition && (
        <FormControl fullWidth sx={{ mt: 2 }}>
          <InputLabel id="condition-label">وضعیت کالا</InputLabel>
        <InputLabel id="condition-label" sx={{ fontFamily: 'Vazirmatn' }}>وضعیت کالا</InputLabel>
          <Select
            labelId="condition-label"
            name="condition"
            value={form.condition}
            onChange={handleSelectChange}
            required
            sx={{ fontFamily: 'Vazirmatn' }}
          >
            <MenuItem value="" sx={{ fontFamily: 'Vazirmatn' }}><em>انتخاب وضعیت</em></MenuItem>
            <MenuItem value="نو" sx={{ fontFamily: 'Vazirmatn' }}>نو</MenuItem>
            <MenuItem value="در حد نو" sx={{ fontFamily: 'Vazirmatn' }}>در حد نو</MenuItem>
            <MenuItem value="کارکرده" sx={{ fontFamily: 'Vazirmatn' }}>کارکرده</MenuItem>
          </Select>
        </FormControl>
      )}

      {/* Custom Fields Section */}
      {form.category && CUSTOM_AD_PROPERTIES[form.category] && (
        <Box sx={{ mt: 2 }}>
          {CUSTOM_AD_PROPERTIES[form.category].map(field => {
            if (["title", "description", "category", "city", "price", "condition", "date_posted", "is_active", "user", "images"].includes(field.name)) return null;
            if (field.type === "number" || field.type === "text") {
              return (
                <TextField
                  key={field.name}
                  label={field.name}
                  value={customFields[field.name] || ''}
                  onChange={e => handleCustomFieldChange(field.name, e.target.value)}
                  type={field.type === "number" ? "number" : "text"}
                  variant="outlined"
                  fullWidth
                  sx={{ mt: 1 }}
                  inputProps={{ style: { textAlign: 'right' } }}
                />
              );
            }
            if (field.type === "select") {
              return (
                <FormControl fullWidth sx={{ mt: 1 }} key={field.name}>
                  <InputLabel sx={{ fontFamily: 'Vazirmatn', color: 'primary.main' }} shrink>
                    {field.name}
                  </InputLabel>
                  <Select
                    value={customFields[field.name] || ''}
                    label={field.name}
                    onChange={e => handleCustomFieldChange(field.name, e.target.value)}
                    sx={{ fontFamily: 'Vazirmatn', color: 'primary.main' }}
                    notched
                  >
                    <MenuItem value="" sx={{ fontFamily: 'Vazirmatn', color: 'primary.main' }}><em>انتخاب کنید</em></MenuItem>
                    {field.options.map(opt => (
                      <MenuItem key={opt} value={opt} sx={{ fontFamily: 'Vazirmatn', color: 'primary.main' }}>{opt}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              );
            }
            if (field.type === "switch") {
              // Use string values for Select, convert to boolean in handler
              const val = customFields[field.name];
              return (
                <FormControl fullWidth sx={{ mt: 1 }} key={field.name}>
                  <InputLabel shrink sx={{ fontFamily: 'Vazirmatn', color: 'primary.main' }}>{field.name}</InputLabel>
                  <Select
                    value={val === true ? 'true' : val === false ? 'false' : ''}
                    label={field.name}
                    onChange={e => {
                      const v = e.target.value;
                      handleCustomFieldChange(field.name, v === 'true' ? true : v === 'false' ? false : '');
                    }}
                    sx={{ fontFamily: 'Vazirmatn', color: 'primary.main' }}
                    notched
                  >
                    <MenuItem value="" sx={{ fontFamily: 'Vazirmatn', color: 'primary.main' }}><em>انتخاب کنید</em></MenuItem>
                    <MenuItem value="true" sx={{ fontFamily: 'Vazirmatn', color: 'primary.main' }}>دارد</MenuItem>
                    <MenuItem value="false" sx={{ fontFamily: 'Vazirmatn', color: 'primary.main' }}>ندارد</MenuItem>
                  </Select>
                </FormControl>
              );
            }
            return null;
          })}
        </Box>
      )}


      <FormControl fullWidth>
        <InputLabel shrink htmlFor="images">تصاویر</InputLabel>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2, alignItems: 'center' }}>
          {/* Plus button for adding images */}
          {/* Existing images with remove button */}
          {existingImages.map((img, idx) => (
            <Box key={img.id} sx={{ width: 80, height: 80, borderRadius: 2, overflow: 'hidden', boxShadow: 1, border: '1px solid #eee', position: 'relative', order: idx + 1 }}>
              <img src={img.image} alt={`ad-img-${idx}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              <Button size="small" sx={{ position: 'absolute', top: 2, right: 2, minWidth: 24, height: 24, borderRadius: '50%', bgcolor: 'error.main', color: 'white', p: 0, fontSize: 16 }} onClick={() => handleRemoveExistingImage(img.id)}>
                ×
              </Button>
            </Box>
          ))}
          {/* New images preview */}
          {imagePreviews.map((url, idx) => (
            <Box key={url} sx={{ width: 80, height: 80, borderRadius: 2, overflow: 'hidden', boxShadow: 1, border: '1px solid #eee', position: 'relative', order: existingImages.length + idx + 1 }}>
              <img src={url} alt={`ad-img-new-${idx}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              <Button size="small" sx={{ position: 'absolute', top: 2, right: 2, minWidth: 24, height: 24, borderRadius: '50%', bgcolor: 'error.main', color: 'white', p: 0, fontSize: 16 }} onClick={() => {
                setImagePreviews(prev => prev.filter((_, i) => i !== idx));
                setForm(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== idx) }));
              }}>
                ×
              </Button>
            </Box>
          ))}
          {/* Plus button for adding images at the end (left in RTL) */}
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: 2,
              border: '2px dashed #bbb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'primary.main',
              cursor: 'pointer',
              transition: 'border-color 0.2s',
              '&:hover': { borderColor: 'primary.main', color: 'primary.dark' },
              position: 'relative',
              background: '#fafbfc',
              order: existingImages.length + imagePreviews.length + 1
            }}
            onClick={() => document.getElementById('images-input').click()}
          >
            <input
              type="file"
              name="images"
              id="images-input"
              multiple
              accept="image/*"
              onChange={handleChange}
              style={{ display: 'none' }}
            />
            <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="18" cy="18" r="17" stroke="#bbb" strokeWidth="2" fill="#fff" />
              <path d="M18 11V25" stroke="#888" strokeWidth="2.5" strokeLinecap="round"/>
              <path d="M11 18H25" stroke="#888" strokeWidth="2.5" strokeLinecap="round"/>
            </svg>
          </Box>
        </Box>
      </FormControl>

      <Stack direction="row" justifyContent="flex-end">
        <Button type="submit" variant="contained" color="success" size="large" sx={{ borderRadius: 2, fontWeight: 700 }}>
          {adId ? 'به روزکردن آگهی' : 'ثبت آگهی'}
        </Button>
      </Stack>
    </Box>
  );
};

export default AdForm;
